import { classes } from "polytype";
import type { ConversationUserData } from "./types.js";
import type { Conversation } from "../Conversation/index.js";
import type { User } from "../../user/index.js";
import { provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import {
  DataModel,
  extractId,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { File } from "../../file/index.js";

export class ConversationUser extends classes(
  DataModel<ConversationUserData>,
  ReferenceModel,
) {
  public readonly id: string;
  public readonly clearName?: string;
  public readonly avatar?: File;
  public readonly isEmployee?: boolean;

  public constructor(data: ConversationUserData) {
    super([data], [data.userId]);
    this.id = data.userId;
    this.clearName = data.clearName;
    this.avatar = data.avatarRefId ? File.ofId(data.avatarRefId) : undefined;
    this.isEmployee = !!data.department;
  }
}

export class ConversationUserListQuery extends ListQueryModel<null> {
  private readonly conversation: Conversation;

  public constructor(conversation: Conversation) {
    super(null, { dependencies: [conversation.id] });
    this.conversation = conversation;
  }

  public execute = provideReact(async () => {
    const response = await config.behaviors.conversation.getMembers(
      this.conversation.id,
    );
    return new ConversationUserList(
      this.conversation,
      response.map((m) => new ConversationUser(m)),
      response.length,
    );
  }, [this.queryId, "conversationUser"]);

  public readonly includes = provideReact(
    async (otherUser: User | ConversationUser | string) => {
      const { items } = await this.execute();
      return items.some((u) => u.data.userId === extractId(otherUser));
    },
    [this.queryId, "conversationUser"],
  );

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.execute();
    return totalCount;
  }, [this.queryId, "conversationUser"]);
}

export class ConversationUserList extends classes(
  ConversationUserListQuery,
  ListDataModel<ConversationUser>,
) {
  public constructor(
    conversation: Conversation,
    users: ConversationUser[],
    totalCount: number,
  ) {
    super([conversation], [users, totalCount]);
  }
}
